import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, RotateCcw, X, Download } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { fmtDate, istToday } from "@/lib/dateFormat";
import { MultiSelectDropdown, SelectedTags } from "@/components/ui/multi-select-dropdown";
import { SearchableSelect } from "@/components/ui/searchable-select";

const STATUS_COLORS = {
  "Draft": "bg-zinc-200 text-zinc-700", "Approval Requested": "bg-amber-100 text-amber-800",
  "Approved": "bg-blue-100 text-blue-800", "Stock Received": "bg-teal-100 text-teal-800",
  "Closed": "bg-green-100 text-green-800", "Rejected": "bg-red-100 text-red-800",
  "Disposed": "bg-stone-200 text-stone-700",
};

const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(n || 0);

export default function RTOListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roles = user?.roles || [];
  const perms = user?.permissions || [];
  const { getValues, loading: configLoading } = useConfig(ALL_CONFIG_GROUPS);
  const RETURN_REASONS = getValues("return_reasons");
  const RTO_TYPES = getValues("rto_types");
  const INTIMATED_BY = getValues("rto_intimated_by");
  const TRADE_FORMATS = getValues("channel_types");

  const [rtos, setRtos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [cnNotDecided, setCnNotDecided] = useState(false);   // FIX-UP R3d
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [sortCol, setSortCol] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [tradeFormatFilter, setTradeFormatFilter] = useState([]);
  const [channelMultiFilter, setChannelMultiFilter] = useState([]);
  const [allChannels, setAllChannels] = useState([]);

  // All SKUs for the SKU selector
  const [allSKUs, setAllSKUs] = useState([]);

  // Order search autocomplete
  const [orderQuery, setOrderQuery] = useState("");
  const [orderResults, setOrderResults] = useState([]);
  const [showOrderResults, setShowOrderResults] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isPreviousOrder, setIsPreviousOrder] = useState(false);
  const orderSearchRef = useRef(null);

  // Create form
  const EMPTY_FORM = {
    order_id: "", order_number: "", previous_order_reference: "", channel: "",
    customer_id: "", customer_name: "", return_type: "Partial RTO", return_reason: "",
    return_reason_detail: "", intimated_by: "", expected_arrival_date: "",
    return_logistics_cost: 0, return_logistics_transporter: "", notes: "",
    destroyed_at_customer: false,
    rto_items: [{ sku_id: "", sku_name: "", ordered_qty_boxes: 0, returned_qty_boxes: 0, batch_number: "", item_notes: "" }],
  };
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const loadRTOs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (cnNotDecided) params.cn_not_decided = true;    /* FIX-UP R3d */
      if (search) params.search = search;
      const { data } = await api.get("/rtos", { params });
      setRtos(data);
    } catch (err) { console.error("API get /rtos error:", err); } finally { setLoading(false); }
  };

  useEffect(() => { loadRTOs(); }, [statusFilter, search, cnNotDecided]);
  useEffect(() => { api.get("/channels").then(r => setAllChannels(r.data || [])).catch(err => console.error("API get /channels error:", err)); }, []);

  const filteredChannelsRto = tradeFormatFilter.length > 0 ? allChannels.filter(c => tradeFormatFilter.includes(c.type)) : allChannels;
  const channelOptionsRto = filteredChannelsRto.filter(c => c.status === "active").map(c => ({ value: c.name, label: c.name, group: c.type || "Other" }));

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, rto_items: [{ sku_id: "", sku_name: "", ordered_qty_boxes: 0, returned_qty_boxes: 0, batch_number: "", item_notes: "" }] });
    setSelectedOrder(null);
    setIsPreviousOrder(false);
    setOrderQuery("");
    setOrderResults([]);
    if (allSKUs.length === 0) api.get("/catalogue/skus").then(r => setAllSKUs(r.data || [])).catch(err => console.error("API get /catalogue/skus error:", err));
    setShowCreate(true);
  };

  // Order search autocomplete
  const searchOrders = async (q) => {
    setOrderQuery(q);
    if (q.length < 2) { setOrderResults([]); setShowOrderResults(false); return; }
    try {
      const { data } = await api.get(`/orders?search=${encodeURIComponent(q)}&limit=15`);
      const ords = data?.orders || data || [];
      setOrderResults(ords);
      setShowOrderResults(true);
    } catch { setOrderResults([]); }
  };

  const selectOrder = (order) => {
    setSelectedOrder(order);
    setIsPreviousOrder(false);
    setOrderQuery(`${order.order_number} — ${order.customer_name}`);
    setShowOrderResults(false);
    setForm(f => ({
      ...f, order_id: order._id, order_number: order.order_number,
      customer_name: order.customer_name, customer_id: order.customer_id,
      channel: order.channel_name || "",
      rto_items: (order.line_items || []).map(li => ({
        sku_id: li.sku_id, sku_name: li.sku_name, ordered_qty_boxes: li.quantity,
        returned_qty_boxes: 0, batch_number: "", item_notes: "",
      })),
    }));
  };

  const selectPreviousOrder = () => {
    setIsPreviousOrder(true);
    setSelectedOrder(null);
    setOrderQuery("");
    setShowOrderResults(false);
    setForm(f => ({
      ...f, order_id: "", order_number: "Previous Order",
      customer_name: "", customer_id: "", channel: "",
      rto_items: [{ sku_id: "", sku_name: "", ordered_qty_boxes: 0, returned_qty_boxes: 0, batch_number: "", item_notes: "" }],
    }));
  };

  // SKU item helpers
  const updateItem = (idx, field, value) => {
    const d = [...form.rto_items];
    d[idx] = { ...d[idx], [field]: value };
    if (field === "sku_id") {
      const sku = allSKUs.find(s => s.sku_id === value);
      if (sku) d[idx].sku_name = sku.sku_name;
    }
    setForm({ ...form, rto_items: d });
  };
  const addItem = () => setForm({ ...form, rto_items: [...form.rto_items, { sku_id: "", sku_name: "", ordered_qty_boxes: 0, returned_qty_boxes: 0, batch_number: "", item_notes: "" }] });
  const removeItem = (idx) => { if (form.rto_items.length <= 1) return; setForm({ ...form, rto_items: form.rto_items.filter((_, i) => i !== idx) }); };

  const handleCreate = async () => {
    if (!form.return_reason) { toast.error("Return reason is required"); return; }
    if (!form.return_reason_detail?.trim()) { toast.error("Reason details are required"); return; }
    if (!form.intimated_by) { toast.error("Intimated By is required"); return; }
    if (!form.rto_items.some(i => i.returned_qty_boxes > 0)) { toast.error("At least one SKU must have a return quantity"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/rtos", { ...form, rto_items: form.rto_items.filter(i => i.sku_id && i.returned_qty_boxes > 0) });
      toast.success(`RTO ${data.rto_number} created`);
      setShowCreate(false);
      loadRTOs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const isLinkedOrder = !!form.order_id;

  const toggleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } };
  const SH = ({ col, children, className: cls }) => (<th className={`cursor-pointer select-none hover:bg-zinc-50 ${cls || ''}`} onClick={() => toggleSort(col)}>{children} {sortCol === col && <span className="text-[9px]">{sortDir === "asc" ? "\u2191" : "\u2193"}</span>}</th>);
  const sortedRtos = [...rtos].filter(r => {
    if (channelMultiFilter.length > 0 && r.channel && !channelMultiFilter.includes(r.channel)) return false;
    return true;
  }).sort((a, b) => {
    if (!sortCol) return 0;
    const m = {
      rto_num: r => parseInt((r.rto_number || "").replace(/\D/g, "")) || 0,
      customer: r => (r.customer_name || "").toLowerCase(),
      type: r => (r.return_type || "").toLowerCase(),
      reason: r => (r.return_reason || "").toLowerCase(),
      status: r => (r.status || "").toLowerCase(),
      created: r => r.created_at || "",
      initiated_by: r => (r.created_by_name || "").toLowerCase(),
    };
    const fn = m[sortCol]; if (!fn) return 0;
    const va = fn(a), vb = fn(b);
    if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
    return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  });

  return (
    <div data-testid="rto-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Returns (RTO)</h1>
          <p className="text-sm text-zinc-500 mt-1">Return to Origin — manage stock returns</p>
        </div>
        <div className="flex gap-2">
          {(roles.includes("Admin") || roles.includes("admin") || perms.includes("rto.export")) && <Button variant="outline" className="rounded-sm h-8 text-xs" data-testid="export-rto-btn" onClick={() => {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (search) params.set("search", search);
            api.get(`/rtos/export?${params.toString()}`, { responseType: "blob" }).then(r => {
              const url = window.URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement("a"); a.href = url; a.download = `RTO_${istToday()}.xlsx`; a.click();
              toast.success("RTOs exported");
            }).catch(err => toast.error(err.response?.status === 400 ? "No data to export" : "Export failed"));
          }}><Download size={14} /> Export Excel</Button>}
          <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-rto-btn">
            <Plus size={14} /> New RTO
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-8 text-xs rounded-sm" data-testid="rto-search" />
        </div>
        <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 h-8 text-xs rounded-sm" data-testid="rto-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Approval Requested">Pending Approval</SelectItem><SelectItem value="Approved">Approved</SelectItem><SelectItem value="Stock Received">Stock Received</SelectItem><SelectItem value="Disposed">Disposed</SelectItem><SelectItem value="Closed">Closed</SelectItem><SelectItem value="Rejected">Rejected</SelectItem></SelectContent>
        </Select>
        <Button variant={cnNotDecided ? "default" : "outline"} className="h-8 text-xs rounded-sm"
                onClick={() => setCnNotDecided(v => !v)} data-testid="rto-cn-not-decided-filter">
          Credit note not decided
        </Button>
        <MultiSelectDropdown label="Trade Format" options={TRADE_FORMATS} selected={tradeFormatFilter} onChange={v => { setTradeFormatFilter(v); setChannelMultiFilter([]); }} />
        <MultiSelectDropdown label="Sales Channel" options={channelOptionsRto} selected={channelMultiFilter} onChange={setChannelMultiFilter} groupBy="group" />
        {(statusFilter || tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && <Button variant="ghost" className="h-8 text-[10px] text-zinc-500" onClick={() => { setStatusFilter(""); setTradeFormatFilter([]); setChannelMultiFilter([]); }}>Clear</Button>}
      </div>
      {(tradeFormatFilter.length > 0 || channelMultiFilter.length > 0) && (
        <div className="flex flex-wrap gap-1 mb-2">
          <SelectedTags selected={tradeFormatFilter} options={TRADE_FORMATS} onRemove={v => setTradeFormatFilter(tradeFormatFilter.filter(x => x !== v))} />
          <SelectedTags selected={channelMultiFilter} options={channelOptionsRto} onRemove={v => setChannelMultiFilter(channelMultiFilter.filter(x => x !== v))} />
        </div>
      )}

      {/* RTO Table */}
      <div className="border border-zinc-200 overflow-x-auto" data-testid="rto-table">
        <table className="data-table w-full">
          <thead><tr><SH col="rto_num">RTO #</SH><th>Order</th><SH col="customer">Customer</SH><th>Channel</th><SH col="type">Type</SH><SH col="reason">Reason</SH><th className="num">Boxes</th><SH col="status">Status</SH><SH col="initiated_by">Initiated By</SH><SH col="created">Created</SH></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={10} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            sortedRtos.length === 0 ? <tr><td colSpan={10} className="text-center py-8 text-zinc-400 text-xs">No RTOs found</td></tr> :
            sortedRtos.map(r => (
              <tr key={r._id} className="cursor-pointer hover:bg-zinc-50" onClick={() => navigate(`/returns/${r._id}`)} data-testid={`rto-row-${r._id}`}>
                <td><span className="font-mono text-xs font-semibold">{r.rto_number}</span> <span className="px-1 py-0.5 text-[8px] font-bold bg-red-100 text-red-700 rounded-sm ml-1">RTO</span></td>
                <td className="text-xs">{r.order_number || "Previous"}</td>
                <td className="text-xs font-medium">{r.customer_name}</td>
                <td className="text-xs">{r.channel || "\u2014"}</td>
                <td className="text-xs">{r.return_type}</td>
                <td className="text-xs text-zinc-600">{r.return_reason}</td>
                <td className="num font-mono text-xs">{r.total_boxes_returned}</td>
                <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${STATUS_COLORS[r.status] || ''}`}>{r.status}</span></td>
                <td className="text-xs text-zinc-500">{r.created_by_name || "\u2014"}</td>
                <td className="text-[10px] text-zinc-500">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create RTO Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="create-rto-dialog">
          <DialogHeader><DialogTitle className="font-heading">New RTO Request</DialogTitle></DialogHeader>
          <div className="space-y-3">

            {/* Fix 4: Searchable Order Autocomplete */}
            <div>
              <Label className="text-xs">Linked Order</Label>
              <div className="relative mt-1" ref={orderSearchRef}>
                {isPreviousOrder ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-600 bg-zinc-100 px-2 py-1 rounded-sm flex-1">Previous Order (not in ORBIT)</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setIsPreviousOrder(false); setForm(f => ({ ...f, order_id: "", order_number: "" })); }}><X size={12} /></Button>
                  </div>
                ) : isLinkedOrder ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-600 bg-blue-50 px-2 py-1 rounded-sm flex-1 border border-blue-200">{form.order_number} — {form.customer_name}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setForm(f => ({ ...f, order_id: "", order_number: "", customer_name: "", customer_id: "", channel: "", rto_items: [{ sku_id: "", sku_name: "", ordered_qty_boxes: 0, returned_qty_boxes: 0, batch_number: "", item_notes: "" }] })); setOrderQuery(""); setSelectedOrder(null); }}><X size={12} /></Button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <Input value={orderQuery} onChange={e => searchOrders(e.target.value)} onFocus={() => { if (orderResults.length) setShowOrderResults(true); }}
                        className="pl-8 h-8 text-xs rounded-sm" placeholder="Type order number or customer name..." data-testid="rto-order-search" />
                    </div>
                    {showOrderResults && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-48 overflow-y-auto" data-testid="order-search-results">
                        <button onClick={selectPreviousOrder} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-100 text-amber-700 font-medium" data-testid="select-previous-order">
                          Previous Order (not in ORBIT)
                        </button>
                        {orderResults.length === 0 && orderQuery.length >= 2 && <p className="px-3 py-2 text-xs text-zinc-400">No orders found</p>}
                        {orderResults.map(o => (
                          <button key={o._id} onClick={() => selectOrder(o)} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-50 flex items-center gap-3" data-testid={`order-result-${o._id}`}>
                            <span className="font-mono font-semibold w-20">{o.order_number}</span>
                            <span className="flex-1 truncate">{o.customer_name}</span>
                            <span className="text-zinc-400">{fmtDate(o.order_date)}</span>
                            <span className="font-mono text-zinc-500">{fmt(o.invoice_value)}</span>
                            <span className={`px-1 py-0.5 text-[8px] font-semibold rounded-sm ${STATUS_COLORS[o.status] || 'bg-zinc-100 text-zinc-600'}`}>{o.status}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {isPreviousOrder && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Previous Order Reference</Label><Input value={form.previous_order_reference} onChange={e => setForm({...form, previous_order_reference: e.target.value})} className="mt-1 rounded-sm text-xs" placeholder="PO number or reference" /></div>
                <div><Label className="text-xs">Customer Name</Label><Input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} className="mt-1 rounded-sm text-xs" /></div>
              </div>
            )}
            {isLinkedOrder && <div><Label className="text-xs">Customer</Label><Input value={form.customer_name} readOnly className="mt-1 rounded-sm text-xs bg-zinc-50" /></div>}

            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Return Type</Label>
                <Select value={form.return_type} onValueChange={v => setForm({...form, return_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{(RTO_TYPES.length ? RTO_TYPES : ["Full RTO", "Partial RTO"]).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Return Reason <span className="text-red-500">*</span></Label>
                <Select value={form.return_reason || "none"} onValueChange={v => setForm({...form, return_reason: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-1 rounded-sm text-xs" data-testid="rto-reason"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-- Select --</SelectItem>{RETURN_REASONS.filter(r => r.toLowerCase() !== "other" && r.toLowerCase() !== "others").map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Intimated By <span className="text-red-500">*</span></Label>
                <Select value={form.intimated_by || "none"} onValueChange={v => setForm({...form, intimated_by: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-1 rounded-sm text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-- Select --</SelectItem>{INTIMATED_BY.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {/* Destroyed at customer checkbox */}
            <label className={`flex items-start gap-2 p-2 border rounded-sm cursor-pointer transition-colors ${form.destroyed_at_customer ? 'border-amber-300 bg-amber-50' : 'border-zinc-200 hover:bg-zinc-50'}`}>
              <input type="checkbox" checked={form.destroyed_at_customer} onChange={e => setForm({...form, destroyed_at_customer: e.target.checked})} className="mt-0.5 accent-amber-600" />
              <div><span className="text-xs font-medium">Stock to be destroyed at customer location (no warehouse return)</span>{form.destroyed_at_customer && <p className="text-[9px] text-amber-700 mt-0.5">Stock will be marked as disposed after approval. No warehouse receive required.</p>}</div>
            </label>
            <div><Label className="text-xs">Reason Details <span className="text-red-500">*</span></Label><Textarea value={form.return_reason_detail} onChange={e => setForm({...form, return_reason_detail: e.target.value})} className="mt-1 rounded-sm text-xs" rows={2} placeholder="Provide detailed reason for the return..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Expected Arrival Date</Label><Input type="date" value={form.expected_arrival_date} onChange={e => setForm({...form, expected_arrival_date: e.target.value})} className="mt-1 rounded-sm text-xs" /></div>
              <div><Label className="text-xs">Return Logistics Cost (INR)</Label><Input type="number" step="0.01" value={form.return_logistics_cost} onChange={e => setForm({...form, return_logistics_cost: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm text-xs" /></div>
            </div>

            {/* Fix 1: SKU Items with proper SKU selector */}
            <div>
              <Label className="text-xs font-semibold">Return Items</Label>
              <div className="border border-zinc-200 mt-1 overflow-visible">
                <table className="data-table w-full">
                  <thead><tr><th className="w-1/3">SKU</th><th className="num w-16">Ordered</th><th className="num w-20">Returning *</th><th className="w-24">Batch</th><th>Notes</th><th className="w-8"></th></tr></thead>
                  <tbody>
                    {form.rto_items.map((item, idx) => (
                      <tr key={idx} data-testid={`rto-item-row-${idx}`}>
                        <td style={{overflow: 'visible', position: 'relative'}}>
                          {isLinkedOrder ? (
                            <span className="text-xs">{item.sku_name || item.sku_id}</span>
                          ) : (
                            <SearchableSelect
                              options={allSKUs}
                              value={item.sku_id || ""}
                              onChange={v => updateItem(idx, "sku_id", v)}
                              placeholder="Search SKU..."
                              displayKey="sku_name"
                              valueKey="sku_id"
                              searchKeys={["sku_id", "sku_name"]}
                              renderOption={s => <span>{s.sku_name} <span className="text-zinc-400 font-mono">({s.sku_id})</span></span>}
                              className="min-w-[200px]"
                            />
                          )}
                        </td>
                        <td className="num font-mono text-xs">
                          {isLinkedOrder ? item.ordered_qty_boxes : (
                            <Input type="number" min={0} value={item.ordered_qty_boxes} onChange={e => updateItem(idx, "ordered_qty_boxes", parseInt(e.target.value) || 0)} className="h-7 w-16 text-xs rounded-sm text-right" />
                          )}
                        </td>
                        <td><Input type="number" min={0} value={item.returned_qty_boxes} onChange={e => updateItem(idx, "returned_qty_boxes", parseInt(e.target.value) || 0)} className="h-7 w-20 text-xs rounded-sm text-right" data-testid={`rto-returning-qty-${idx}`} /></td>
                        <td><Input value={item.batch_number} onChange={e => updateItem(idx, "batch_number", e.target.value)} className="h-7 text-xs rounded-sm" placeholder="Batch" /></td>
                        <td><Input value={item.item_notes} onChange={e => updateItem(idx, "item_notes", e.target.value)} className="h-7 text-xs rounded-sm" placeholder="Notes" /></td>
                        <td>{form.rto_items.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removeItem(idx)}><X size={12} /></Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="ghost" className="h-6 text-[10px] mt-1 text-zinc-600" onClick={addItem} data-testid="add-rto-sku-btn">+ Add SKU</Button>
            </div>

            <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 rounded-sm text-xs" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleCreate} disabled={submitting} className="rounded-sm text-xs" data-testid="save-rto-btn">{submitting ? "Creating..." : "Create RTO"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
